import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Clock, LogIn, LogOut, Calendar, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const TimeTracking = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [activeShift, setActiveShift] = useState(null);
  const [entries, setEntries] = useState([]);
  const [clockInNotes, setClockInNotes] = useState('');
  const [clockOutNotes, setClockOutNotes] = useState('');
  const [breakMinutes, setBreakMinutes] = useState(30);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    loadData();
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const loadData = async () => {
    try {
      const [shiftRes, entriesRes] = await Promise.all([
        axios.get('/time/active-shift'),
        axios.get('/time/my-entries')
      ]);
      
      if (shiftRes.data.active) {
        setActiveShift(shiftRes.data.shift);
      } else {
        setActiveShift(null);
      }
      
      setEntries(entriesRes.data);
    } catch (error) {
      console.error('Error loading time tracking data:', error);
      toast.error(t('error.loading'));
    } finally {
      setLoading(false);
    }
  };

  const getLocation = () => {
    return new Promise((resolve) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              lat: position.coords.latitude,
              lng: position.coords.longitude
            });
          },
          () => resolve(null)
        );
      } else {
        resolve(null);
      }
    });
  };

  const handleClockIn = async () => {
    try {
      const location = await getLocation();
      
      await axios.post('/time/clock-in', {
        notes: clockInNotes,
        location
      });
      
      toast.success(t('timeTracking.clockIn') + ' ✓');
      setClockInNotes('');
      loadData();
    } catch (error) {
      console.error('Error clocking in:', error);
      toast.error(error.response?.data?.detail || t('error.saving'));
    }
  };

  const handleClockOut = async () => {
    if (!window.confirm(t('timeTracking.clockOut') + '?')) return;

    try {
      const location = await getLocation();
      
      const res = await axios.post('/time/clock-out', {
        break_minutes: breakMinutes,
        notes: clockOutNotes,
        location
      });
      
      toast.success(res.data.message);
      setClockOutNotes('');
      setBreakMinutes(30);
      loadData();
    } catch (error) {
      console.error('Error clocking out:', error);
      toast.error(error.response?.data?.detail || t('error.saving'));
    }
  };

  const calculateWorkedTime = (clockIn) => {
    if (!clockIn) return '0:00';
    const start = new Date(clockIn);
    const diff = currentTime - start;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  };

  const formatDuration = (hours) => {
    if (!hours) return '0:00';
    const h = Math.floor(Math.abs(hours));
    const m = Math.round((Math.abs(hours) - h) * 60);
    return `${hours < 0 ? '-' : ''}${h}:${m.toString().padStart(2, '0')}`;
  };

  const groupByDate = (entries) => {
    const grouped = {};
    entries.forEach(entry => {
      const date = new Date(entry.clock_in).toLocaleDateString('de-DE');
      if (!grouped[date]) grouped[date] = [];
      grouped[date].push(entry);
    });
    return grouped;
  };

  const calculateWeekTotal = () => {
    const oneWeekAgo = new Date(currentTime - 7 * 24 * 60 * 60 * 1000);
    return entries
      .filter(e => new Date(e.clock_in) > oneWeekAgo && e.status === 'completed')
      .reduce((sum, e) => sum + (e.total_hours || 0), 0);
  };

  const calculateMonthTotal = () => {
    const currentMonth = currentTime.getMonth();
    const currentYear = currentTime.getFullYear();
    return entries
      .filter(e => {
        const d = new Date(e.clock_in);
        return d.getMonth() === currentMonth && d.getFullYear() === currentYear && e.status === 'completed';
      })
      .reduce((sum, e) => sum + (e.total_hours || 0), 0);
  };

  const groupedEntries = groupByDate(entries);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 max-w-6xl mx-auto">
      <div>
        <h1 className="text-4xl font-bold text-white flex items-center gap-3">
          <Clock className="h-10 w-10" />
          Arbeitszeiterfassung
        </h1>
        <p className="text-zinc-400 mt-2">
          {currentTime.toLocaleDateString('de-DE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          {' • '}
          {currentTime.toLocaleTimeString('de-DE')}
        </p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-400">Heute</p>
                <p className="text-2xl font-bold text-white">
                  {activeShift ? calculateWorkedTime(activeShift.clock_in) : '0:00'}
                </p>
              </div>
              <Clock className="h-8 w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-400">Diese Woche</p>
                <p className="text-2xl font-bold text-white">
                  {formatDuration(calculateWeekTotal())}
                </p>
              </div>
              <Calendar className="h-8 w-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-400">Dieser Monat</p>
                <p className="text-2xl font-bold text-white">
                  {formatDuration(calculateMonthTotal())}
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-purple-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Clock In/Out Section */}
      {activeShift ? (
        <Card className="bg-green-900/20 border-green-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Clock className="h-6 w-6 text-green-400" />
              Aktive Schicht
            </CardTitle>
            <CardDescription>
              Eingestempelt: {new Date(activeShift.clock_in).toLocaleString('de-DE')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-zinc-900/50 rounded-lg">
              <p className="text-sm text-zinc-400 mb-1">Gearbeitete Zeit</p>
              <p className="text-4xl font-bold text-white">
                {calculateWorkedTime(activeShift.clock_in)}
              </p>
            </div>

            {activeShift.notes && (
              <div>
                <Label className="text-zinc-300">Notiz beim Einstempeln</Label>
                <p className="text-sm text-zinc-400 mt-1">{activeShift.notes}</p>
              </div>
            )}

            <div>
              <Label htmlFor="breakMinutes" className="text-zinc-300">Pause (Minuten)</Label>
              <input
                id="breakMinutes"
                type="number"
                value={breakMinutes}
                onChange={(e) => setBreakMinutes(parseInt(e.target.value) || 0)}
                className="w-full mt-1 p-2 bg-zinc-950 border border-zinc-800 rounded-md text-white"
                min="0"
              />
            </div>

            <div>
              <Label htmlFor="clockOutNotes" className="text-zinc-300">Notiz (optional)</Label>
              <Textarea
                id="clockOutNotes"
                value={clockOutNotes}
                onChange={(e) => setClockOutNotes(e.target.value)}
                placeholder="z.B. Überstunden, besondere Vorkommnisse..."
                className="mt-1 bg-zinc-950 border-zinc-800 text-white"
                rows={2}
              />
            </div>

            <Button
              onClick={handleClockOut}
              size="lg"
              className="w-full bg-red-600 hover:bg-red-700"
            >
              <LogOut className="mr-2 h-5 w-5" />
              Ausstempeln
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-blue-900/20 border-blue-700">
          <CardHeader>
            <CardTitle className="text-white">Schicht starten</CardTitle>
            <CardDescription>Stempeln Sie ein, um Ihre Arbeitszeit zu erfassen</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="clockInNotes" className="text-zinc-300">Notiz (optional)</Label>
              <Textarea
                id="clockInNotes"
                value={clockInNotes}
                onChange={(e) => setClockInNotes(e.target.value)}
                placeholder="z.B. Außendienst, Filiale Nord..."
                className="mt-1 bg-zinc-950 border-zinc-800 text-white"
                rows={2}
              />
            </div>

            <Button
              onClick={handleClockIn}
              size="lg"
              className="w-full bg-green-600 hover:bg-green-700"
            >
              <LogIn className="mr-2 h-5 w-5" />
              Einstempeln
            </Button>
          </CardContent>
        </Card>
      )}

      {/* History */}
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-white">Verlauf</h2>
        
        {Object.keys(groupedEntries).length === 0 ? (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-12 text-center">
              <Clock className="h-16 w-16 text-zinc-700 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">Noch keine Einträge</h3>
              <p className="text-zinc-400">Ihre Zeiterfassungen werden hier angezeigt</p>
            </CardContent>
          </Card>
        ) : (
          Object.entries(groupedEntries).map(([date, dateEntries]) => (
            <Card key={date} className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white">{date}</CardTitle>
                  <Badge className="bg-blue-900/30 text-blue-400 border-blue-700">
                    {dateEntries.filter(e => e.status === 'completed').reduce((sum, e) => sum + (e.total_hours || 0), 0).toFixed(2)}h
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dateEntries.map(entry => (
                    <div
                      key={entry.entry_id}
                      className={`p-4 rounded-lg ${
                        entry.status === 'active' 
                          ? 'bg-green-900/20 border-2 border-green-700' 
                          : 'bg-zinc-800 border-2 border-transparent'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <p className="font-semibold text-white">
                              {new Date(entry.clock_in).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                              {' → '}
                              {entry.clock_out 
                                ? new Date(entry.clock_out).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
                                : 'Aktiv'
                              }
                            </p>
                            {entry.status === 'active' && (
                              <Badge className="bg-green-900/30 text-green-400 border-green-700">
                                Läuft
                              </Badge>
                            )}
                          </div>
                          
                          {entry.total_hours !== null && (
                            <p className="text-sm text-zinc-400">
                              Dauer: <span className="font-semibold text-white">{formatDuration(entry.total_hours)}</span>
                              {entry.break_minutes > 0 && ` (${entry.break_minutes} Min. Pause)`}
                            </p>
                          )}
                          
                          {entry.notes && (
                            <p className="text-sm text-zinc-500 mt-1">{entry.notes}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default TimeTracking;
